import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icon, Modal, Select, Spin, Tooltip, message } from 'antd'
import ImageCropperModal from '../../../../components/ImageCropperModal'
import { usePresignedUpload } from '../../hooks/usePresignedUpload'
import ShotFrameGenerateModal from './ShotFrameGenerateModal'
import {
  FRAME_LABELS,
  FRAME_ROLES,
  normalizeFrameUrl,
  normalizeShotFrames
} from './frameUtils'
import './ShotFrameModal.scss'

const { Option } = Select

const buildDraftFrame = (url, previousFrame, source, extras = {}) => {
  if (!url) return null

  return {
    ...(previousFrame || {}),
    ...extras,
    url,
    source,
    updatedAt: Date.now()
  }
}

const FramePreview = ({ frame, label, emptyText }) => {
  const url = normalizeFrameUrl(frame)

  if (!url) {
    return (
      <div className="shot-frame-preview shot-frame-preview--empty">
        <Icon type="picture" />
        <span>{emptyText}</span>
      </div>
    )
  }

  return (
    <div className="shot-frame-preview">
      <img src={url} alt={label} loading="lazy" decoding="async" />
    </div>
  )
}

const FrameTile = ({
  role,
  frame,
  active,
  compact = false,
  onClick,
  onRemove
}) => {
  const hasImage = !!normalizeFrameUrl(frame)
  const canRemove = role === FRAME_ROLES.end && hasImage

  return (
    <button
      type="button"
      className={`shot-frame-tile ${active ? 'is-active' : ''} ${
        hasImage ? 'has-image' : 'is-empty'
      } ${compact ? 'shot-frame-tile--compact' : ''}`}
      onClick={onClick}
    >
      <FramePreview
        frame={frame}
        label={FRAME_LABELS[role]}
        emptyText={role === FRAME_ROLES.end ? 'Add End' : 'No Start'}
      />
      <span className="shot-frame-tile__label">{FRAME_LABELS[role]}</span>
      {canRemove && (
        <Tooltip title="Remove end frame">
          <span
            className="shot-frame-tile__remove"
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              onRemove()
            }}
          >
            <Icon type="close" />
          </span>
        </Tooltip>
      )}
    </button>
  )
}

export const ShotFrameImageOverlay = ({
  activeRole,
  hasEndFrame,
  disabled = false,
  onRoleChange
}) => {
  if (!hasEndFrame) return null

  return (
    <div className="shot-frame-image-overlay">
      <div className="shot-frame-image-overlay__scrim" />
      <div
        className="shot-frame-image-overlay__switch"
        role="group"
        aria-label="Frame preview"
      >
        <span className="shot-frame-image-overlay__switch-label">Frame</span>
        <div className="shot-frame-image-overlay__switch-options">
          <button
            type="button"
            className={
              activeRole === FRAME_ROLES.start ? 'is-active' : undefined
            }
            onClick={() => onRoleChange(FRAME_ROLES.start)}
            disabled={disabled}
            aria-pressed={activeRole === FRAME_ROLES.start}
          >
            Start
          </button>
          <button
            type="button"
            className={
              activeRole === FRAME_ROLES.end ? 'is-active' : undefined
            }
            onClick={() => onRoleChange(FRAME_ROLES.end)}
            disabled={disabled}
            aria-pressed={activeRole === FRAME_ROLES.end}
          >
            End
          </button>
        </div>
      </div>
    </div>
  )
}

const ShotFrameModal = ({
  visible,
  shot,
  shotLabel,
  aspectRatio,
  copyTargets = [],
  initialRole = FRAME_ROLES.end,
  videoId,
  formData,
  imageProvider,
  disabled = false,
  onCancel,
  onSave,
  onOpenImageEditor
}) => {
  const [draftFrames, setDraftFrames] = useState(() =>
    normalizeShotFrames(shot)
  )
  const [activeRole, setActiveRole] = useState(initialRole)
  const [copyValue, setCopyValue] = useState(undefined)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [uploadRole, setUploadRole] = useState(initialRole)
  const [cropperVisible, setCropperVisible] = useState(false)
  const [generateModalVisible, setGenerateModalVisible] = useState(false)
  const fileInputRef = useRef(null)
  const { uploadFiles, isUploading } = usePresignedUpload({ videoId })

  useEffect(() => {
    if (!visible) return

    setDraftFrames(normalizeShotFrames(shot))
    setActiveRole(initialRole || FRAME_ROLES.end)
    setCopyValue(undefined)
  }, [visible, shot, initialRole])

  const historyImages = useMemo(() => {
    const seen = new Set()

    return (shot?.images_history || [])
      .filter(item => item?.url)
      .filter(item => {
        if (seen.has(item.url)) return false
        seen.add(item.url)
        return true
      })
      .slice()
      .reverse()
  }, [shot?.images_history])

  const activeFrameUrl = normalizeFrameUrl(draftFrames[activeRole])
  const hasEndFrame = !!normalizeFrameUrl(draftFrames.end)
  const canSwap =
    !!normalizeFrameUrl(draftFrames.start) &&
    !!normalizeFrameUrl(draftFrames.end)

  const setFrame = (role, url, source = 'manual', extras = {}) => {
    setDraftFrames(prev => ({
      ...prev,
      [role]: buildDraftFrame(url, prev[role], source, extras)
    }))
  }

  const handleRemoveEnd = () => {
    setDraftFrames(prev => ({ ...prev, end: null }))
    setActiveRole(FRAME_ROLES.end)
  }

  const handleSwap = () => {
    if (!canSwap) return

    setDraftFrames(prev => ({
      start: buildDraftFrame(normalizeFrameUrl(prev.end), prev.end, 'swap'),
      end: buildDraftFrame(normalizeFrameUrl(prev.start), prev.start, 'swap')
    }))
  }

  const handleCopyTarget = value => {
    const selected = copyTargets.find(target => target.value === value)
    if (!selected?.imageSrc) return

    setFrame(activeRole, selected.imageSrc, 'copy')
    setCopyValue(undefined)
  }

  const handleUseStartFrame = () => {
    const startUrl = normalizeFrameUrl(draftFrames.start)
    if (!startUrl) {
      message.warning('Add a start frame before copying it.')
      return
    }

    setFrame(activeRole, startUrl, 'copy')
  }

  const triggerFileUpload = () => {
    if (disabled || isUploading) return
    setUploadRole(activeRole)
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = event => {
    const file = event.target.files?.[0]

    if (file) {
      setSelectedImageFile(file)
      setCropperVisible(true)
    }

    event.target.value = ''
  }

  const handleCropComplete = async croppedFile => {
    if (!croppedFile) return

    try {
      const response = await uploadFiles({
        files: [croppedFile],
        category: 'image'
      })

      if (!response.success || !response.urls?.[0]) {
        throw response.error || new Error('Upload failed')
      }

      setFrame(uploadRole, response.urls[0], 'upload')
      message.success(`${FRAME_LABELS[uploadRole]} uploaded`)
    } catch (error) {
      message.error(error?.message || 'Frame upload failed')
    } finally {
      setCropperVisible(false)
      setSelectedImageFile(null)
    }
  }

  const handleOpenImageEditor = () => {
    if (!onOpenImageEditor) return

    onSave(draftFrames)
    onOpenImageEditor(activeRole)
  }

  const handleGeneratedFrame = (imageUrl, generationPrompt) => {
    if (!imageUrl) return

    setFrame(activeRole, imageUrl, 'generation', { prompt: generationPrompt })
    setGenerateModalVisible(false)
  }

  const handleSave = () => {
    if (!normalizeFrameUrl(draftFrames.start)) {
      message.warning('A shot still needs a start frame.')
      return
    }

    onSave(draftFrames)
    onCancel()
  }

  return (
    <>
      <Modal
        visible={visible}
        title={null}
        width={920}
        centered
        destroyOnClose
        maskClosable={false}
        className="shot-frame-modal"
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel} disabled={isUploading}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            disabled={disabled || isUploading}
          >
            Save Frames
          </Button>
        ]}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="image/*"
        />

        <div className="shot-frame-modal__header">
          <div>
            <div className="shot-frame-modal__eyebrow">{shotLabel}</div>
            <h3>Start and End Frames</h3>
          </div>
          <div className="shot-frame-modal__status">
            {hasEndFrame ? (
              <span className="shot-frame-modal__status-pill is-active">
                Two-frame shot
              </span>
            ) : (
              <span className="shot-frame-modal__status-pill">
                Single start frame
              </span>
            )}
          </div>
        </div>

        <div className="shot-frame-modal__body">
          <div className="shot-frame-modal__stage">
            <div className="shot-frame-modal__tiles">
              <FrameTile
                role={FRAME_ROLES.start}
                frame={draftFrames.start}
                active={activeRole === FRAME_ROLES.start}
                onClick={() => setActiveRole(FRAME_ROLES.start)}
                onRemove={() => {}}
              />
              <FrameTile
                role={FRAME_ROLES.end}
                frame={draftFrames.end}
                active={activeRole === FRAME_ROLES.end}
                onClick={() => setActiveRole(FRAME_ROLES.end)}
                onRemove={handleRemoveEnd}
              />
            </div>

            <div className="shot-frame-modal__preview">
              {isUploading && uploadRole === activeRole ? (
                <div className="shot-frame-modal__uploading">
                  <Spin />
                  <span>Uploading frame...</span>
                </div>
              ) : (
                <FramePreview
                  frame={draftFrames[activeRole]}
                  label={FRAME_LABELS[activeRole]}
                  emptyText={
                    activeRole === FRAME_ROLES.end
                      ? 'No end frame yet'
                      : 'No start frame'
                  }
                />
              )}
              <span className="shot-frame-modal__preview-tag">
                {FRAME_LABELS[activeRole]}
              </span>
            </div>
          </div>

          <aside className="shot-frame-modal__tools">
            <div className="shot-frame-modal__active-row">
              <span>Editing</span>
              <strong>{FRAME_LABELS[activeRole]}</strong>
            </div>

            <div className="shot-frame-modal__tool-grid">
              <Button
                icon="thunderbolt"
                onClick={() => setGenerateModalVisible(true)}
                disabled={disabled || isUploading}
              >
                Generate
              </Button>
              <Button
                icon="edit"
                onClick={handleOpenImageEditor}
                disabled={disabled || isUploading}
              >
                Edit
              </Button>
              <Button
                icon="upload"
                onClick={triggerFileUpload}
                disabled={disabled || isUploading}
              >
                Upload
              </Button>
              <Button
                icon="copy"
                onClick={handleUseStartFrame}
                disabled={
                  disabled ||
                  isUploading ||
                  activeRole === FRAME_ROLES.start ||
                  !normalizeFrameUrl(draftFrames.start)
                }
              >
                Use Start
              </Button>
              <Button
                icon="swap"
                onClick={handleSwap}
                disabled={disabled || isUploading || !canSwap}
              >
                Swap
              </Button>
            </div>

            <div className="shot-frame-modal__field">
              <label>Copy From Existing Frame</label>
              <Select
                showSearch
                allowClear
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 360, maxWidth: 520 }}
                value={copyValue}
                placeholder="Select a project frame"
                onChange={handleCopyTarget}
                disabled={disabled || isUploading || copyTargets.length === 0}
                filterOption={(input, option) =>
                  String(option.props.children || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {copyTargets.map(target => (
                  <Option
                    key={target.value}
                    value={target.value}
                    title={target.label}
                  >
                    {target.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="shot-frame-modal__history">
              <div className="shot-frame-modal__history-header">
                <span>Image History</span>
                <small>{historyImages.length}</small>
              </div>
              {historyImages.length > 0 ? (
                <div className="shot-frame-modal__history-grid">
                  {historyImages.map(item => (
                    <button
                      type="button"
                      key={item.url}
                      onClick={() => setFrame(activeRole, item.url, 'history')}
                      className={
                        activeFrameUrl === item.url ? 'is-selected' : ''
                      }
                    >
                      <img src={item.url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="shot-frame-modal__empty-history">
                  No saved image history yet
                </div>
              )}
            </div>
          </aside>
        </div>
      </Modal>

      <ImageCropperModal
        visible={cropperVisible}
        imageFile={selectedImageFile}
        aspectRatio={aspectRatio || '16:9'}
        onCancel={() => {
          setCropperVisible(false)
          setSelectedImageFile(null)
        }}
        onCrop={handleCropComplete}
      />

      <ShotFrameGenerateModal
        visible={generateModalVisible}
        shot={shot}
        frameRole={activeRole}
        frame={draftFrames[activeRole]}
        fallbackFrame={draftFrames.start}
        formData={formData}
        imageProvider={imageProvider}
        videoId={videoId}
        disabled={disabled}
        onCancel={() => setGenerateModalVisible(false)}
        onGenerated={handleGeneratedFrame}
      />
    </>
  )
}

export default ShotFrameModal
